import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Settings } from 'lucide-react';
import { settingsApi } from '@/api-client';

interface NavbarProps {
  cartCount: number;
  onCartClick: () => void;
}

export function Navbar({ cartCount, onCartClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopEnabled, setShopEnabled] = useState(true);
  const [shopChecked, setShopChecked] = useState(false);
  const location = useLocation();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchShopStatus = useCallback(() => {
    settingsApi.getShopStatus()
      .then(d => {
        const isEnabled = d?.enabled === true;
        console.log('[Navbar] Shop-Status:', isEnabled, 'Raw:', d);
        setShopEnabled(isEnabled);
        setShopChecked(true);
      })
      .catch(err => {
        console.error('[Navbar] API-Fehler:', err);
        setShopEnabled(false);
        setShopChecked(true);
      });
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  useEffect(() => {
    fetchShopStatus();
    intervalRef.current = setInterval(fetchShopStatus, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchShopStatus]);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) fetchShopStatus();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchShopStatus]);

  const navLinks = [
    { to: '/', label: 'Startseite' },
    { to: '/shop', label: 'Shop' },
    { to: '/ueber-uns', label: 'Über uns' },
    { to: '/kontakt', label: 'Kontakt' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const showCart = !shopChecked || shopEnabled;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${scrolled ? 'glass border-white/[0.06]' : 'bg-transparent border-transparent'}`}>
      <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between h-[70px]">
        <Link to="/" className="font-display text-2xl font-bold italic text-gold tracking-wide">JL-Software</Link>

        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <li key={link.to}>
              <Link to={link.to} className={`relative text-sm font-medium transition-colors ${isActive(link.to) ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>
                {link.label}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/admin" className="flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-gold transition-colors">
              <Settings size={14} />Admin
            </Link>
          </li>
        </ul>

        <div className="flex items-center gap-4">
          {showCart && (
            <button onClick={onCartClick} className="relative p-2 text-text-secondary hover:text-gold transition-colors" aria-label="Warenkorb">
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-crimson text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              )}
            </button>
          )}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 text-text-secondary hover:text-text-primary transition-colors" aria-label="Menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden glass border-t border-white/[0.06]">
          <ul className="px-6 py-4 flex flex-col gap-3">
            {navLinks.map(link => (
              <li key={link.to}>
                <Link to={link.to} className={`block py-2 text-sm font-medium transition-colors ${isActive(link.to) ? 'text-gold' : 'text-text-secondary hover:text-text-primary'}`}>{link.label}</Link>
              </li>
            ))}
            <li><Link to="/admin" className="flex items-center gap-2 py-2 text-sm font-medium text-text-muted hover:text-gold transition-colors"><Settings size={14} />Admin</Link></li>
            {showCart && (
              <li>
                <button onClick={() => { onCartClick(); setMobileOpen(false); }} className="flex items-center gap-2 py-2 text-sm font-medium text-text-secondary hover:text-gold transition-colors">
                  <ShoppingCart size={14} />Warenkorb {cartCount > 0 && `(${cartCount})`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </nav>
  );
}